import { Project, TimeEntry, Quote } from '../types';

export const calculateWorkingHours = (timeEntries: TimeEntry[]): number => {
  return timeEntries.reduce((total, entry) => {
    if (entry.duration) {
      return total + entry.duration / 60; // Convert minutes to hours
    }
    
    if (entry.startTime && entry.endTime) {
      const start = new Date(entry.startTime).getTime();
      const end = new Date(entry.endTime).getTime();
      return total + (end - start) / (1000 * 60 * 60); // Convert ms to hours
    }
    
    return total;
  }, 0);
};

export const calculateMaterialsCost = (project: Project): number => {
  return project.materials.reduce((total, material) => {
    return total + (material.quantity * material.unitPrice);
  }, 0);
};

export const calculateLaborCost = (project: Project): number => {
  if (project.isFixedPrice && project.fixedPrice) {
    return project.fixedPrice;
  }
  
  // Calculate labor cost based on individual team member rates and hours
  return project.timeEntries.reduce((total, entry) => {
    if (entry.teamMembers && entry.teamMembers.length > 0) {
      // New system: calculate based on team member rates
      return total + entry.teamMembers.reduce((entryTotal, memberEntry) => {
        let hoursWorked: number;
        
        if (memberEntry.hoursWorked !== undefined) {
          // Use specific hours for this member
          hoursWorked = memberEntry.hoursWorked;
        } else if (entry.duration) {
          // Use duration in minutes, convert to hours
          hoursWorked = entry.duration / 60;
        } else if (entry.startTime && entry.endTime) {
          // Calculate from time range
          const start = new Date(entry.startTime).getTime();
          const end = new Date(entry.endTime).getTime();
          hoursWorked = (end - start) / (1000 * 60 * 60);
        } else {
          hoursWorked = 0;
        }
        
        return entryTotal + (hoursWorked * memberEntry.hourlyRate);
      }, 0);
    } else {
      // Legacy system: fallback to project hourly rate
      let hours: number;
      
      if (entry.duration) {
        hours = entry.duration / 60;
      } else if (entry.startTime && entry.endTime) {
        const start = new Date(entry.startTime).getTime();
        const end = new Date(entry.endTime).getTime();
        hours = (end - start) / (1000 * 60 * 60);
      } else {
        hours = 0;
      }
      
      return total + (hours * project.hourlyRate);
    }
  }, 0);
};

export const calculateProjectTotal = (project: Project): number => {
  const materialsCost = calculateMaterialsCost(project);
  
  if (project.isFixedPrice && project.fixedPrice) {
    return materialsCost + project.fixedPrice;
  }
  
  const laborCost = calculateLaborCost(project);
  return materialsCost + laborCost;
};

export const calculateQuoteTotal = (quote: Quote): { 
  subtotal: number; 
  taxAmount: number; 
  discountAmount: number; 
  total: number 
} => {
  const materialsCost = quote.materials.reduce((total, material) => 
    total + (material.quantity * material.unitPrice), 0
  );
  
  const laborCost = quote.laborItems?.reduce((total, item) => 
    total + item.amount, 0
  ) || 0;
  
  const subtotal = materialsCost + laborCost + quote.fixedPrice;
  
  // Calculate discount first (applied to subtotal)
  const discountAmount = subtotal * (quote.discountRate / 100);
  
  // Calculate tax (applied after discount)
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (quote.taxRate / 100);
  
  // Calculate total
  const total = taxableAmount + taxAmount;
  
  return {
    subtotal,
    taxAmount,
    discountAmount,
    total
  };
};